<template>
  <form class="space-y-5" @submit.prevent="submit">
    <!-- Name Fields -->
    <div class="grid grid-cols-1 gap-5 sm:grid-cols-1">
      <div class="space-y-1">
        <label for="first_name" class="block text-sm font-semibold text-gray-700">
          First Name
        </label>
        <div class="relative group">
          <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-200">
            <FontAwesomeIcon icon="user" class="h-4 w-4 text-gray-400 group-focus-within:text-green-500" />
          </div>
          <input
            id="first_name"
            v-model="firstName"
            type="text"
            required
            class="block w-full pl-10 pr-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-green-500/20 focus:border-green-500 placeholder-gray-400 transition-all duration-300 text-gray-900"
            placeholder="John"
          />
        </div>
      </div>

      <div class="space-y-1">
        <label for="last_name" class="block text-sm font-semibold text-gray-700">
          Last Name
        </label>
        <div class="relative group">
          <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-200">
            <FontAwesomeIcon icon="id-card" class="h-4 w-4 text-gray-400 group-focus-within:text-green-500" />
          </div>
          <input
            id="last_name"
            v-model="lastName"
            type="text"
            required
            class="block w-full pl-10 pr-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-green-500/20 focus:border-green-500 placeholder-gray-400 transition-all duration-300 text-gray-900"
            placeholder="Doe"
          />
        </div>
      </div>
    </div>

    <!-- Username Field -->
    <div class="space-y-1">
      <div class="flex items-center justify-between">
        <label for="username" class="block text-sm font-semibold text-gray-700">Username</label>
        <span class="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">3-30 chars</span>
      </div>
      <div class="relative group">
        <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-200">
          <FontAwesomeIcon icon="user-tag" class="h-4 w-4 text-gray-400 group-focus-within:text-green-500" />
        </div>
        <input
          id="username"
          v-model="username"
          type="text"
          required
          minlength="3"
          maxlength="30"
          pattern="[a-zA-Z0-9_]+"
          title="Only letters, numbers and underscores"
          class="block w-full pl-10 pr-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-green-500/20 focus:border-green-500 placeholder-gray-400 transition-all duration-300 text-gray-900"
          placeholder="johndoe123"
        />
      </div>
    </div>

    <!-- Email Field -->
    <div class="space-y-1">
      <label for="email" class="block text-sm font-semibold text-gray-700">
        Email Address
      </label>
      <div class="relative group">
        <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-200">
          <FontAwesomeIcon icon="envelope" class="h-4 w-4 text-gray-400 group-focus-within:text-green-500" />
        </div>
        <input
          id="email"
          v-model="email"
          type="email"
          required
          class="block w-full pl-10 pr-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-green-500/20 focus:border-green-500 placeholder-gray-400 transition-all duration-300 text-gray-900"
          placeholder="you@example.com"
        />
      </div>
    </div>

    <!-- Password Fields -->
    <div class="grid grid-cols-1 gap-5 sm:grid-cols-1">
      <div class="space-y-1">
        <label for="password" class="block text-sm font-semibold text-gray-700">
          Password
        </label>
        <div class="relative group">
          <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-200">
            <FontAwesomeIcon icon="lock" class="h-4 w-4 text-gray-400 group-focus-within:text-green-500" />
          </div>
          <input
            id="password"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            required
            minlength="8"
            class="block w-full pl-10 pr-10 py-3 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-green-500/20 focus:border-green-500 placeholder-gray-400 transition-all duration-300 text-gray-900"
            placeholder="••••••••"
          />
          <button
            type="button"
            @click="showPassword = !showPassword"
            class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <FontAwesomeIcon :icon="showPassword ? 'eye-slash' : 'eye'" class="h-4 w-4" />
          </button>
        </div>
      </div>

      <div class="space-y-1">
        <label for="confirm_password" class="block text-sm font-semibold text-gray-700">
          Confirm
        </label>
        <div class="relative group">
          <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-200">
            <FontAwesomeIcon icon="lock" class="h-4 w-4 text-gray-400 group-focus-within:text-green-500" />
          </div>
          <input
            id="confirm_password"
            v-model="confirmPassword"
            :type="showPassword ? 'text' : 'password'"
            required
            class="block w-full pl-10 pr-10 py-3 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-green-500/20 focus:border-green-500 placeholder-gray-400 transition-all duration-300 text-gray-900"
            placeholder="••••••••"
          />
        </div>
      </div>
    </div>

    <!-- Password Strength Indicator -->
    <div v-if="password" class="space-y-2 pt-1 animate-fade-in">
      <div class="flex items-center justify-between text-xs">
        <span class="text-gray-500 font-medium">Strength</span>
        <span class="font-bold" :class="passwordStrength.color">{{ passwordStrength.text }}</span>
      </div>
      <div class="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div 
          class="h-full transition-all duration-500 ease-out rounded-full"
          :class="passwordStrength.barColor"
          :style="{ width: passwordStrength.percentage + '%' }"
        ></div>
      </div>
    </div>

    <!-- Submit Button -->
    <div class="pt-4">
      <button
        type="submit"
        :disabled="isLoading"
        class="w-full flex justify-center items-center py-4 px-6 rounded-xl bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white font-bold text-lg shadow-lg hover:shadow-green-500/30 transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
      >
        <template v-if="isLoading">
          <FontAwesomeIcon icon="circle-notch" spin class="mr-2 h-5 w-5" />
          Creating Account...
        </template>
        <template v-else>
          Create Account
        </template>
      </button>
    </div>

    <!-- Terms Agreement -->
    <div class="text-center pt-2">
      <p class="text-xs text-gray-500">
        By registering, you agree to our
        <a href="#" class="font-bold text-green-600 hover:text-green-700 transition-colors">Terms</a>
        and
        <a href="#" class="font-bold text-green-600 hover:text-green-700 transition-colors">Privacy Policy</a>
      </p>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faEnvelope,
  faLock,
  faUser,
  faIdCard,
  faEye,
  faEyeSlash,
  faCircleNotch,
  faUserTag,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons'

library.add(
  faEnvelope,
  faLock,
  faUser,
  faIdCard,
  faEye,
  faEyeSlash,
  faCircleNotch,
  faUserTag,
  faUserPlus,
)

const emit = defineEmits<{
  (
    e: 'submit',
    userData: {
      username: string
      email: string
      password: string
      confirmPassword: string
      first_name: string
      last_name: string
    },
  ): void
  (e: 'error', message: string): void
}>()

const username = ref('')
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const firstName = ref('')
const lastName = ref('')
const isLoading = ref(false)
const showPassword = ref(false)

// Password strength calculator
const passwordStrength = computed(() => {
  const pass = password.value
  let score = 0
  let percentage = 0
  let text = 'Weak'
  let color = 'text-red-500'
  let barColor = 'bg-red-500'

  if (pass.length >= 8) score++
  if (/[A-Z]/.test(pass)) score++
  if (/[a-z]/.test(pass)) score++
  if (/[0-9]/.test(pass)) score++
  if (/[^A-Za-z0-9]/.test(pass)) score++

  switch (score) {
    case 0:
    case 1:
      percentage = 20
      text = 'Weak'
      color = 'text-red-500'
      barColor = 'bg-red-500'
      break
    case 2:
    case 3:
      percentage = 50
      text = 'Fair'
      color = 'text-yellow-500'
      barColor = 'bg-yellow-500'
      break
    case 4:
      percentage = 80
      text = 'Good'
      color = 'text-green-500'
      barColor = 'bg-green-500'
      break
    case 5:
      percentage = 100
      text = 'Strong'
      color = 'text-emerald-600'
      barColor = 'bg-emerald-600'
      break
  }

  return { percentage, text, color, barColor }
})

const submit = async () => {
  if (password.value !== confirmPassword.value) {
    emit('error', 'Passwords do not match')
    return
  }

  isLoading.value = true
  try {
    await emit('submit', {
      username: username.value,
      email: email.value,
      password: password.value,
      confirmPassword: confirmPassword.value,
      first_name: firstName.value,
      last_name: lastName.value,
    })
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
/* Custom animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out forwards;
}
</style> 